import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { Skeleton } from '@cortex/web-design-system/components/ui/Skeleton';
import { ProtectedRoute } from '../components/auth/index.js';
import { AssignmentHistoryTable } from '../components/features/project/AssignmentHistoryTable.js';
import { useAssignmentHistory } from '../hooks/useAssignmentHistory.js';

/**
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business アサイン履歴ページ。全体PJ看板の「案件をアサインする」で成功したアサインを、案件番号・案件名・アサイン日・役割ごとのメンバー（要件定義開発者 / 開発者 / レビュワー）で並び替え・絞り込みして読み、各行にアサイン意図を保存する。プロジェクト管理フォルダ配下で、閲覧はリーダー以上と PMO のみ
 * @graph-connects useAssignmentHistory [calls] アサイン履歴の取得
 * @graph-connects AssignmentHistoryTable [calls] アサイン履歴テーブルの描画
 */
export const Route = createFileRoute('/projects/assignment-history')({
  component: AssignmentHistoryPage,
});

/** @graph-connects AssignmentHistoryContent [calls] 履歴本体の描画 */
function AssignmentHistoryPage() {
  const { t } = useTranslation();
  return (
    <ProtectedRoute pageKey="assignmentHistory">
      <div className="space-y-6 animate-fade-in">
        <h1 className="text-2xl font-bold text-[var(--color-text-primary)] animate-content-reveal">
          {t('assignmentHistory.title')}
        </h1>
        <AssignmentHistoryContent />
      </div>
    </ProtectedRoute>
  );
}

/**
 * 履歴の取得状態に応じて本体を描く（ProtectedRoute の内側で取得し、権限の無い人には API を叩かせない）。
 * @graph-connects useAssignmentHistory [calls] アサイン履歴の取得
 */
function AssignmentHistoryContent() {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useAssignmentHistory();
  return (
    <Card>
      <CardContent className="p-0 sm:p-2">
        {isLoading ? (
          <div className="space-y-2 p-4">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        ) : isError || !data ? (
          <p role="alert" className="text-center py-8 text-[var(--color-error)]">
            {t('assignmentHistory.loadFailed')}
          </p>
        ) : (
          <AssignmentHistoryTable items={data.items} memberTeams={data.memberTeams} />
        )}
      </CardContent>
    </Card>
  );
}
